// F1 App-Shell (TES-948): geteilter Nav-Link mit Offline-Guard.
// Von SideNav (QM/Admin/Super/Platform) und NavDrawer genutzt — eine Quelle,
// keine Duplikation. Variante "desktop" (collapsible, Tooltip) vs "drawer".
// TES-969 (D7): optionale Count-Pill (withBadge) fuer die operative Haupt-Nav im
// Drawer — gleiche Daten/Farb-/Pulse-Logik + data-testid wie die Desktop-SideNav
// (getNavBadgeInfo/NavBadgePill), Mobile-Paritaet zum Industrial-Mock.
// Render in Sub-Komponenten + geteilte Bausteine zerlegt, damit keine Funktion
// die fallow-CRAP-Schwelle reisst — Verhalten/Markup unveraendert.
import { Link, useLocation } from "react-router";
import { cn } from "@/lib/utils";
import type { ShellModel } from "./useShellModel";
import type { NavItem } from "./navConfig";
import { getNavBadgeInfo, type NavBadgeInfo } from "./navBadges";
import { NavBadgePill } from "./NavBadge";
import { NavIcon, NavLabel, NavTooltip } from "./navLinkParts";

const ACTIVE_LINK =
  "border-l-4 border-secondary active-nav-glow bg-sidebar-accent text-sidebar-accent-foreground";
const INACTIVE_LINK =
  "hover:bg-sidebar-accent text-sidebar-foreground/80 hover:text-sidebar-foreground";

interface NavLinkBaseProps {
  item: NavItem;
  model: ShellModel;
  variant: "desktop" | "drawer";
  collapsed?: boolean;
  /** drawer: offline-guard respektiert item.offlineEnabled (Haupt-Nav). */
  offlineRespectsItem?: boolean;
  /** drawer-Haupt-Nav: Count-Pill wie in der Desktop-SideNav rendern (TES-969). */
  withBadge?: boolean;
  onNavigate?: () => void;
  dataTour?: string;
}

// Offline-Guard: drawer (Haupt-Nav) respektiert item.offlineEnabled, desktop nicht (1:1).
function computeOfflineDisabled(
  isOnline: boolean,
  item: NavItem,
  offlineRespectsItem: boolean,
): boolean {
  if (isOnline) return false;
  if (offlineRespectsItem) return !item.offlineEnabled;
  return true;
}

function paddingFor(iconCollapsed: boolean): string {
  return iconCollapsed ? "px-2 justify-center" : "px-3";
}

// Aktiver Link: Basis-Layout + Padding + Active/Inactive-Stil (module-scope wie
// paddingFor, haelt NavLinkBase unter der fallow-CRAP-Schwelle).
function linkClassName(iconCollapsed: boolean, isActive: boolean): string {
  return cn(
    "flex items-center gap-3 py-2.5 rounded-lg transition-all duration-200",
    paddingFor(iconCollapsed),
    isActive ? ACTIVE_LINK : INACTIVE_LINK,
  );
}

// Icon + Label-Body, geteilt zwischen disabled-Div und aktivem Link.
// Mit badge rendert rechts die Count-Pill (Label streckt via flex-1, TES-969).
function NavLinkBody({
  item,
  model,
  iconCollapsed,
  badge,
}: {
  item: NavItem;
  model: ShellModel;
  iconCollapsed: boolean;
  badge?: NavBadgeInfo;
}) {
  return (
    <>
      <NavIcon item={item} collapsed={iconCollapsed} />
      <NavLabel hidden={iconCollapsed} className={badge ? "flex-1" : undefined}>
        {model.t(item.nameKey)}
      </NavLabel>
      {badge && <NavBadgePill badge={badge} href={item.href} />}
    </>
  );
}

// Offline-deaktivierter Eintrag; im desktop-Modus zusaetzlich mit Tooltip umhuellt.
function NavLinkDisabled({
  item,
  model,
  iconCollapsed,
  collapsed,
  isDrawer,
  dataTour,
}: {
  item: NavItem;
  model: ShellModel;
  iconCollapsed: boolean;
  collapsed: boolean;
  isDrawer: boolean;
  dataTour?: string;
}) {
  const disabled = (
    <div
      data-tour={dataTour}
      aria-label={iconCollapsed ? model.t(item.nameKey) : undefined}
      aria-disabled="true"
      className={cn(
        "flex items-center gap-3 py-2.5 rounded-lg cursor-not-allowed opacity-40 text-sidebar-foreground/50",
        paddingFor(iconCollapsed),
      )}
    >
      <NavLinkBody item={item} model={model} iconCollapsed={iconCollapsed} />
    </div>
  );
  if (isDrawer) return disabled;
  return (
    <NavTooltip collapsed={collapsed} text={model.t("nav.offlineDisabled")}>
      {disabled}
    </NavTooltip>
  );
}

// TES-1664: pre-existing nav state branching; this PR only preserves collapsed link names.
// fallow-ignore-next-line complexity
export function NavLinkBase({
  item,
  model,
  variant,
  collapsed = false,
  offlineRespectsItem = false,
  withBadge = false,
  onNavigate,
  dataTour,
}: NavLinkBaseProps) {
  const location = useLocation();
  const { isOnline } = model;
  const isActive = location.pathname === item.href;
  const isOfflineDisabled = computeOfflineDisabled(isOnline, item, offlineRespectsItem);
  const isDrawer = variant === "drawer";
  const iconCollapsed = !isDrawer && collapsed;
  // Pill nur am aktiven Link (disabled-Variante bleibt badge-los — 1:1 wie SideNavItemDisabled).
  const badge = withBadge ? getNavBadgeInfo(item.href, model) : undefined;

  if (isOfflineDisabled) {
    return (
      <NavLinkDisabled
        item={item}
        model={model}
        iconCollapsed={iconCollapsed}
        collapsed={collapsed}
        isDrawer={isDrawer}
        dataTour={dataTour}
      />
    );
  }

  return (
    <Link
      to={item.href}
      data-tour={dataTour}
      onClick={onNavigate}
      aria-label={iconCollapsed ? model.t(item.nameKey) : undefined}
      className={linkClassName(iconCollapsed, isActive)}
    >
      <NavLinkBody item={item} model={model} iconCollapsed={iconCollapsed} badge={badge} />
    </Link>
  );
}
